/* eslint-disable no-extend-native */
export {};
declare global {
    interface Date {
        isDate(): boolean;
        toDate(): Date;
        toTime(): Date;
        dateOnly(): Date;
        timeOnly(): Date;
        compare(value: Date | undefined, ignoreSeconds?: boolean): number;
        compareDate(value: Date | undefined): number;
        lessDate(value: Date | undefined): boolean;
        moreDate(value: Date | undefined): boolean;
        compareTime(value: Date | undefined): number;
        betweenDates(from: Date, to: Date): boolean;
        dayDiff(value: Date | undefined): number;
        addDays(value: number): Date;
        addMinutes(value: number): Date;
        addSeconds(value: number): Date;
        addMonths(value: number): Date;
        millisecondsBetween(value: Date): number;
        secondsBetween(value: Date): number;
        minutesBetween(value: Date): number;
        hoursBetween(value: Date): number;
        daysBetween(value: Date): number;
        equals(value: Date | undefined, ignoreSeconds?: boolean): boolean;
        equalsDate(value: Date | undefined): boolean;
        mergeDate(value: Date | undefined): Date;
        mergeTime(value: Date | undefined): Date;
        format(format: string): string;
        toServer(): Date;
        toLocal(): Date;
        toServerTime(): Date;
        toLocale(): string;
        toLocaleShort(): string;
        toLocaleLong(): string;
        toLocaleShortDate(): string;
        toLocaleMediumDate(): string;
        toLocaleShortTime(): string;
        toLocaleMediumTime(): string;
        toLocalePrint(): string;
        toTimestamp(): string;
    }
}

if (!Date.prototype.isDate) {
    Date.prototype.isDate = function isDate(this: Date): boolean {
        return true;
    };
}

if (!Date.prototype.toDate) {
    Date.prototype.toDate = function toDate(this: Date): Date {
        return this;
    };
}

if (!Date.prototype.toTime) {
    Date.prototype.toTime = function toTime(this: Date): Date {
        return this;
    };
}

if (!Date.prototype.dateOnly) {
    Date.prototype.dateOnly = function dateOnly(this: Date): Date {
        return new Date(
            this.getFullYear(),
            this.getMonth(),
            this.getDate(),
            0,
            0,
            0,
        );
    };
}

if (!Date.prototype.timeOnly) {
    Date.prototype.timeOnly = function timeOnly(this: Date): Date {
        const now = new Date();
        return new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            this.getHours(),
            this.getMinutes(),
            this.getSeconds(),
        );
    };
}

if (!Date.prototype.compare) {
    Date.prototype.compare = function compare(
        this: Date,
        value: Date | undefined,
        ignoreSeconds = false,
    ): number {
        const time1 = new Date(
            this.getFullYear(),
            this.getMonth(),
            this.getDate(),
            this.getHours(),
            this.getMinutes(),
            ignoreSeconds ? 0 : this.getSeconds(),
        );
        const time2 = new Date(
            value?.getFullYear() ?? 0,
            value?.getMonth() ?? 0,
            value?.getDate() ?? 0,
            value?.getHours() ?? 0,
            value?.getMinutes() ?? 0,
            ignoreSeconds ? 0 : value?.getSeconds() ?? 0,
        );
        return time1.getTime() < time2.getTime()
            ? -1
            : time1.getTime() === time2.getTime()
            ? 0
            : 1;
    };
}

if (!Date.prototype.compareDate) {
    Date.prototype.compareDate = function compareDate(
        this: Date,
        value: Date | undefined,
    ): number {
        return this.dateOnly().compare(value?.dateOnly());
    };
}

if (!Date.prototype.lessDate) {
    Date.prototype.lessDate = function lessDate(
        this: Date,
        value: Date | undefined,
    ): boolean {
        return this.compareDate(value) < 0;
    };
}

if (!Date.prototype.moreDate) {
    Date.prototype.moreDate = function moreDate(
        this: Date,
        value: Date | undefined,
    ): boolean {
        return this.compareDate(value) > 0;
    };
}

if (!Date.prototype.compareTime) {
    Date.prototype.compareTime = function compareTime(
        this: Date,
        value: Date | undefined,
    ): number {
        return this.timeOnly().compare(value?.timeOnly());
    };
}

if (!Date.prototype.betweenDates) {
    Date.prototype.betweenDates = function betweenDates(
        from: Date,
        to: Date,
    ): boolean {
        return this.compareDate(from) >= 0 && this.compareDate(to) <= 0;
    };
}

if (!Date.prototype.dayDiff) {
    Date.prototype.dayDiff = function dayDiff(
        this: Date,
        value: Date | undefined,
    ): number {
        return Math.floor(
            Math.abs(
                this.dateOnly().getTime() - (value?.dateOnly()?.getTime() ?? 0),
            ) /
                (1000 * 60 * 60 * 24),
        );
    };
}

if (!Date.prototype.addMinutes) {
    Date.prototype.addMinutes = function addMinutes(
        this: Date,
        value: number,
    ): Date {
        return new Date(this.getTime() + value * 60000);
    };
}

if (!Date.prototype.addSeconds) {
    Date.prototype.addSeconds = function addSeconds(
        this: Date,
        value: number,
    ): Date {
        return new Date(this.getTime() + value * 1000);
    };
}

if (!Date.prototype.addDays) {
    Date.prototype.addDays = function addDays(this: Date, value: number): Date {
        let temp = structuredClone(this);
        temp.setDate(temp.getDate() + value);
        return temp;
    };
}

if (!Date.prototype.addMonths) {
    Date.prototype.addMonths = function addMonths(
        this: Date,
        value: number,
    ): Date {
        let temp = structuredClone(this);
        const targetMonth = temp.getMonth() + value;
        temp.setMonth(targetMonth);
        if (temp.getMonth() !== targetMonth) temp.setDate(temp.getDate() + -1);
        return temp;
    };
}

if (!Date.prototype.millisecondsBetween) {
    Date.prototype.millisecondsBetween = function millisecondsBetween(
        this: Date,
        value: Date,
    ): number {
        return this.getTime() - value.getTime();
    };
}

if (!Date.prototype.secondsBetween) {
    Date.prototype.secondsBetween = function secondsBetween(
        this: Date,
        value: Date,
    ): number {
        return (this.getTime() - value.getTime()) / 1000;
    };
}

if (!Date.prototype.minutesBetween) {
    Date.prototype.minutesBetween = function minutesBetween(
        this: Date,
        value: Date,
    ): number {
        return this.secondsBetween(value) / 60;
    };
}

if (!Date.prototype.hoursBetween) {
    Date.prototype.hoursBetween = function hoursBetween(
        this: Date,
        value: Date,
    ): number {
        return this.minutesBetween(value) / 60;
    };
}

if (!Date.prototype.daysBetween) {
    Date.prototype.daysBetween = function daysBetween(
        this: Date,
        value: Date,
    ): number {
        return this.hoursBetween(value) / 24;
    };
}

if (!Date.prototype.equals) {
    Date.prototype.equals = function equals(
        this: Date,
        value: Date | undefined,
        ignoreSeconds = false,
    ): boolean {
        return this.compare(value, ignoreSeconds) === 0;
    };
}

if (!Date.prototype.equalsDate) {
    Date.prototype.equalsDate = function equalsDate(
        this: Date,
        value: Date | undefined,
    ): boolean {
        return this.compareDate(value) === 0;
    };
}

if (!Date.prototype.mergeDate) {
    Date.prototype.mergeDate = function mergeDate(
        this: Date,
        value: Date | undefined
    ): Date {
        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate(),
            this?.getHours() ?? 0,
            this?.getMinutes() ?? 0,
            this?.getSeconds() ?? 0,
        );
    };
}

if (!Date.prototype.mergeTime) {
    Date.prototype.mergeTime = function mergeTime(
        this: Date,
        value: Date | undefined
    ): Date {
        return new Date(
            this.getFullYear(),
            this.getMonth(),
            this.getDate(),
            value?.getHours() ?? 0,
            value?.getMinutes() ?? 0,
            value?.getSeconds() ?? 0,
        );
    };
}

if (!Date.prototype.format) {
    Date.prototype.format = function toString(
        this: Date,
        format: string,
    ): string {
        format = format + "";

        let MONTH_NAMES = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        let DAY_NAMES = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sun",
            "Mon",
            "Tue",
            "Wed",
            "Thu",
            "Fri",
            "Sat",
        ];
        let DAY_SUFFIXES = ["st", "nd", "rd", "th"];

        function LZ(x: number) {
            return (x < 0 || x > 9 ? "" : "0") + x;
        }

        let result = "";
        let i_format = 0;
        let current = "";
        let token = "";
        let year = this.getFullYear() + "";
        let month = this.getMonth() + 1;
        let date = this.getDate();
        let dateLeast =
            date > 29
                ? date - 30
                : date > 19
                ? date - 20
                : date > 9
                ? date - 10
                : date;
        let day = this.getDay();
        let hour = this.getHours();
        let minute = this.getMinutes();
        let second = this.getSeconds();
        if (year.length < 4) {
            year = "" + (Number(year) - 0 + 1900);
        }

        let value = new Map<string, any>();

        value.set("y", "" + year);
        value.set("yy", year.substring(2, 4));
        value.set("yyy", year.substring(1, 4));
        value.set("yyyy", year);
        value.set("M", month);
        value.set("MM", LZ(month));
        value.set("MMM", MONTH_NAMES[month - 1].substring(0, 3));
        value.set("MMMM", MONTH_NAMES[month - 1]);
        value.set("NNN", MONTH_NAMES[month + 11]);
        value.set("d", date);
        value.set("dd", LZ(date));
        value.set("ddd", DAY_NAMES[day].substring(0, 3));
        value.set("dddd", DAY_NAMES[day]);
        value.set("E", DAY_NAMES[day + 7]);
        value.set("EE", DAY_NAMES[day]);
        value.set("x", DAY_SUFFIXES[dateLeast - 1] || DAY_SUFFIXES[3]);
        value.set("H", hour);
        value.set("HH", LZ(hour));
        value.set("m", minute);
        value.set("mm", LZ(minute));
        value.set("s", second);
        value.set("ss", LZ(second));
        value.set("t", second);
        value.set("tt", LZ(second));
        if (hour === 0) {
            value.set("h", 12);
        } else if (hour > 12) {
            value.set("h", hour - 12);
        } else {
            value.set("h", hour);
        }
        value.set("hh", LZ(value.get("h")));
        if (hour > 11) {
            value.set("tt", "PM");
        } else {
            value.set("tt", "AM");
        }
        value.set("t", value.get("tt").substring(0, 1));

        while (i_format < format.length) {
            current = format.charAt(i_format);
            token = "";
            while (
                format.charAt(i_format) === current &&
                i_format < format.length
            ) {
                token += format.charAt(i_format++);
            }
            if (!!value.get(token)) {
                result = result + value.get(token);
            } else {
                result = result + token;
            }
        }
        return result;
    };
}

if (!Date.prototype.toServer) {
    Date.prototype.toServer = function toServer(this: Date): Date {
        return new Date(
            `${this.getFullYear()}-${(this.getMonth() + 1).pad(
                2,
            )}-${this.getDate().pad(2)}T00:00:00Z`,
        );
    };
}

if (!Date.prototype.toLocal) {
    Date.prototype.toLocal = function toLocal(this: Date): Date {
        return new Date(
            `${this.getFullYear()}-${(this.getMonth() + 1).pad(
                2,
            )}-${this.getDate().pad(2)}T00:00:00`,
        );
    };
}

if (!Date.prototype.toServerTime) {
    Date.prototype.toServerTime = function toServerTime(this: Date): Date {
        return new Date(
            `${this.getFullYear()}-${(this.getMonth() + 1).pad(
                2,
            )}-${this.getDate().pad(2)}T${this.getHours().pad(
                2,
            )}:${this.getMinutes().pad(2)}:${this.getSeconds().pad(2)}Z`,
        );
    };
}

if (!Date.prototype.toLocale) {
    Date.prototype.toLocale = function toLocale(this: Date): string {
        return this.toLocaleDateString(navigator.language);
    };
}

if (!Date.prototype.toLocaleShort) {
    Date.prototype.toLocaleShort = function toLocaleShort(this: Date): string {
        return (
            this.toLocaleDateString(navigator.language, {
                dateStyle: "short",
            } as Intl.DateTimeFormatOptions) +
            " " +
            this.toLocaleTimeString(navigator.language, {
                timeStyle: "short",
            } as Intl.DateTimeFormatOptions)
        );
    };
}

if (!Date.prototype.toLocaleLong) {
    Date.prototype.toLocaleLong = function toLocaleLong(this: Date): string {
        return (
            this.toLocaleDateString(navigator.language, {
                dateStyle: "long",
            } as Intl.DateTimeFormatOptions) +
            " " +
            this.toLocaleTimeString(navigator.language, {
                timeStyle: "short",
            } as Intl.DateTimeFormatOptions)
        );
    };
}

if (!Date.prototype.toLocaleShortDate) {
    Date.prototype.toLocaleShortDate = function toLocaleShortDate(
        this: Date,
    ): string {
        return this.toLocaleDateString(navigator.language, {
            dateStyle: "short",
        } as Intl.DateTimeFormatOptions);
    };
}

if (!Date.prototype.toLocaleMediumDate) {
    Date.prototype.toLocaleMediumDate = function toLocaleMediumDate(
        this: Date,
    ): string {
        return this.toLocaleDateString(navigator.language, {
            dateStyle: "medium",
        } as Intl.DateTimeFormatOptions);
    };
}

if (!Date.prototype.toLocaleShortTime) {
    Date.prototype.toLocaleShortTime = function toLocaleShortTime(
        this: Date,
    ): string {
        return this.toLocaleTimeString(navigator.language, {
            timeStyle: "short",
        } as Intl.DateTimeFormatOptions);
    };
}

if (!Date.prototype.toLocaleMediumTime) {
    Date.prototype.toLocaleMediumTime = function toLocaleMediumTime(
        this: Date,
    ): string {
        return this.toLocaleTimeString(navigator.language, {
            timeStyle: "medium",
        } as Intl.DateTimeFormatOptions);
    };
}

if (!Date.prototype.toLocalePrint) {
    Date.prototype.toLocalePrint = function toLocalePrint(this: Date): string {
        return this.toLocaleMediumTime().toUpperCase() + " " + this.toLocale();
    };
}

if (!Date.prototype.toTimestamp) {
    Date.prototype.toTimestamp = function toTimestamp(this: Date): string {
        return this.toISOString().replace(/:/g, "-").replace(".", "-");
    };
}
